import React, { useCallback, useEffect, useRef } from "react";

import { Stack, StackProps } from "@mui/material";
import { Notice } from "components/Prompts/Notice";
import { useIsViewerPermission, usePrompt } from "hooks";

interface Props {
  dropId: string;
  containerSx?: StackProps["sx"];
  children?: React.ReactNode;
  onDragOver?: StackProps["onDragOver"];
  onDragLeave?: StackProps["onDragLeave"];
  onFilesDrop?: (files: FileList) => void | Promise<void>;
  onHTMLDrop?: (value: string) => void | Promise<void>;
}

const baseDropId = "base_drop_id";

export const DragDrop: React.FC<Props> = ({
  dropId,
  containerSx,
  children,
  onDragOver,
  onDragLeave,
  onFilesDrop,
  onHTMLDrop
}) => {
  // const [draggingOverTarget, setDraggingOverTarget] = useState<boolean>(false);
  const isAnalystViewerPermission = useIsViewerPermission("file_upload");
  const openPrompt = usePrompt();

  const ref = useRef<HTMLDivElement>(null);
  const handleWindowDragOverOrDrag = (event: DragEvent) => {
    // This prevents the browser from trying to load whatever file the user dropped on the window
    if ((event.target as HTMLElement).id.includes("text-section")) return;
    if ((event.target as HTMLElement).parentElement?.id.includes("text-section")) return;
    event.preventDefault();
  };

  const eventHasFiles = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    let hasFiles: boolean = false;
    if (!event.dataTransfer) return hasFiles;

    const types: readonly string[] = event.dataTransfer.types;

    hasFiles = types.some((item) => item === "Files");

    return hasFiles;
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>): void | Promise<void> => {
      if ((event.target as HTMLElement).id.includes("text-section")) return;
      if ((event.target as HTMLElement).parentElement?.id.includes("text-section")) return;
      if (eventHasFiles(event)) {
        if (isAnalystViewerPermission) {
          openPrompt({ Component: Notice, props: { type: "trial" } });
          return;
        }
        const { files } = event.dataTransfer;
        if (!files || !files.length) return;
        return onFilesDrop?.(files);
      } else {
        const html = event.dataTransfer.getData("text/html");
        onHTMLDrop?.(html);
      }
    },
    [onFilesDrop, onHTMLDrop]
  );

  useEffect(() => {
    const { current } = ref;
    if (!current) return;
    current.addEventListener("dragover", handleWindowDragOverOrDrag);
    current.addEventListener("drop", handleWindowDragOverOrDrag);
  }, []);

  useEffect(() => {
    return () => {
      const { current } = ref;
      if (!current) return;
      current.removeEventListener("dragover", handleWindowDragOverOrDrag);
      current.removeEventListener("drop", handleWindowDragOverOrDrag);
    };
  }, []);

  return (
    <Stack
      ref={ref}
      id={`${baseDropId}${dropId}`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={handleDrop}
      sx={{ ...containerSx }}
    >
      {children}
    </Stack>
  );
};
